/**
 * Combo chain tracker.
 *
 * Each merge that lands within `windowMs` of the previous one extends
 * the combo chain and bumps the score multiplier. When the window
 * expires (no merges in time), the chain resets.
 *
 * Multiplier ladder: chain N → MULTIPLIER_LADDER[N] (clamped at the
 * max). Scoring code multiplies raw points by the active multiplier
 * before adding to the score domain.
 *
 * Pure logic — no DOM, no scene. Tests drive it deterministically by
 * passing nowMs explicitly.
 */

export type ComboListener = (state: ComboState) => void;

export interface ComboState {
  /** Active chain depth (0 = no combo, 1 = first merge in window, etc.). */
  chain: number;
  /** Multiplier applied to the next score award. ≥ 1. */
  multiplier: number;
  /** Wall-clock deadline (ms) for the next merge to extend the chain. 0 if idle. */
  expiresAt: number;
}

export interface ComboOptions {
  /** Time window between merges (ms) for the chain to extend. */
  windowMs?: number;
  /** Multiplier per chain depth. Index 0 unused (chain starts at 1). */
  ladder?: ReadonlyArray<number>;
}

const DEFAULT_WINDOW_MS = 2000;
const DEFAULT_LADDER: ReadonlyArray<number> = [1, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];

export interface ComboTracker {
  state(): ComboState;
  /**
   * Register a merge happening at `nowMs`. Returns the multiplier that
   * should be applied to its score award. Call score.add(rawPoints * mult).
   */
  registerMerge(nowMs: number): number;
  /** Per-frame tick — expires the chain if the window has lapsed. */
  tick(nowMs: number): void;
  /** Reset (run restart). */
  reset(): void;
  subscribe(listener: ComboListener): () => void;
}

export function createComboTracker(opts: ComboOptions = {}): ComboTracker {
  const windowMs = opts.windowMs ?? DEFAULT_WINDOW_MS;
  const ladder = opts.ladder ?? DEFAULT_LADDER;
  const maxChain = ladder.length - 1;
  const listeners = new Set<ComboListener>();

  let chain = 0;
  let expiresAt = 0;

  const multiplierFor = (c: number): number => {
    if (c <= 0) return 1;
    if (c >= maxChain) return ladder[maxChain]!;
    return ladder[c]!;
  };

  const snap = (): ComboState => ({
    chain,
    multiplier: multiplierFor(chain),
    expiresAt,
  });

  const announce = (): void => {
    const s = snap();
    for (const l of listeners) l(s);
  };

  const registerMerge = (nowMs: number): number => {
    if (chain > 0 && nowMs > expiresAt) {
      chain = 0;
    }
    chain = Math.min(maxChain, chain + 1);
    expiresAt = nowMs + windowMs;
    announce();
    return multiplierFor(chain);
  };

  const tick = (nowMs: number): void => {
    if (chain > 0 && nowMs > expiresAt) {
      chain = 0;
      expiresAt = 0;
      announce();
    }
  };

  const reset = (): void => {
    if (chain === 0 && expiresAt === 0) return;
    chain = 0;
    expiresAt = 0;
    announce();
  };

  return {
    state: snap,
    registerMerge,
    tick,
    reset,
    subscribe: (l) => {
      listeners.add(l);
      l(snap());
      return () => listeners.delete(l);
    },
  };
}

export const __testing = { DEFAULT_WINDOW_MS, DEFAULT_LADDER };
